import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { IStoreItem } from "../../interfaces/IStoredItem";
import { typedAction } from "../helpers";
import { IPinnedItems } from "../models/reduxModels";
import {
  RESET_USER_PINNED_ITEMS,
  SET_USER_PINNED_ITEMS,
  SET_PINNED_IS_LOADING,
} from "./actionTypes";
import { FileTypeEnum } from "../../components/enums/fileTypeEnum";
import { StoreItemHelper } from "../../components/helpers/storeItemHelper";
import { logger } from "../../../../utilities/logger";
import LZString = require("lz-string");
import { apiGetCompressedPinnedData, apiGetOldPinnedItems, apiSetPinnedItems } from "../../services/ApiService";

const initialState: IPinnedItems = {
  isPinnedItemsLoading: false,
  pinnedItems: null
};
//PINNED ITEMS
//Get, Set, Add, Reset, Delete
const setIsPinnedItemsLoading = (isPinnedItemsLoading: boolean) => {
  return typedAction(SET_PINNED_IS_LOADING, isPinnedItemsLoading);
};

export const setPinnedItems = (pinnedItems: IStoreItem[]) => {
  return typedAction(SET_USER_PINNED_ITEMS, pinnedItems);
};

export const resetPinnedItems = () => {
  return typedAction(RESET_USER_PINNED_ITEMS);
};

export const getPinnedItems = (accountId: string): ThunkAction<Promise<number>, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  if (getState().pinned?.isPinnedItemsLoading) {
    return -1;
  }

  dispatch(setIsPinnedItemsLoading(true));
  let pinnedItems: IStoreItem[];
  try {
    let compressedPinnedData: string = await apiGetCompressedPinnedData(accountId);
    if (compressedPinnedData) {
      let fallback: boolean = false;
      try {
        const decompressedPinned = LZString.decompressFromBase64(compressedPinnedData);
        if (decompressedPinned) {
          pinnedItems = JSON.parse(decompressedPinned);
        }
        else {
          fallback = true;
        }
      } catch (error) {
        fallback = true;
      }
      if (fallback) {
        // Fallback for old structured data
        pinnedItems = await apiGetOldPinnedItems(accountId);
      }
    }
  } catch (error) {
    logger(error, "pinnedItems.ts (getPinnedItems)");
    pinnedItems = null;
  }
  if (pinnedItems?.length > 25) {
    pinnedItems = pinnedItems.slice(0, 25);
  }
  dispatch(setPinnedItems(pinnedItems ?? []));
  return pinnedItems ? pinnedItems.length : 0;
};

export const addPinnedItem = (accountId: string, itemToPin: IStoreItem): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  if (itemToPin) {
    let updatedPinnedItems: IStoreItem[];
    try {
      const itemToPinArray: IStoreItem[] = [itemToPin];
      const existingPinnedItems: IStoreItem[] = (getState().pinned?.pinnedItems ?? []).filter(existingPinnedItem => !StoreItemHelper.itemExists(existingPinnedItem, itemToPinArray));

      updatedPinnedItems = existingPinnedItems
        ? itemToPinArray.concat(existingPinnedItems)
        : itemToPinArray;

      apiSetPinnedItems(accountId, updatedPinnedItems);
    } catch (e) {
      logger(e, "pinnedItems.ts (addPinnedItems)");
    }
    dispatch(setPinnedItems(updatedPinnedItems));
  }
};

export const updatePinnedItems = (accountId: string, pinnedItems: IStoreItem[]): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch) => {
  if (pinnedItems) {
    try {
      apiSetPinnedItems(accountId, pinnedItems);
    } catch (e) {
      logger(e, "pinnedItems.ts (updatePinnedItems)");
    }
    dispatch(setPinnedItems(pinnedItems));
  }
};

export const removePinnedItem = (accountId: string, item: { itemId: string; driveId: string }, fileType: FileTypeEnum): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  const existingSavedItems: IStoreItem[] = getState().pinned.pinnedItems ?? [];
  let updatedItems: IStoreItem[];
  try {
    if (fileType === FileTypeEnum.folder) {
      updatedItems = !item.itemId
        ? existingSavedItems.filter(x => x.driveId !== item.driveId || x.itemId != item.itemId)
        : existingSavedItems.filter(x => x.itemId !== item.itemId);
    }
    else {
      updatedItems = existingSavedItems.filter(x => x.itemId !== item.itemId);
    }

    apiSetPinnedItems(accountId, updatedItems);
  } catch (e) {
    logger(e, "pinnedItems.ts (removePinnedItems)");
  }

  dispatch(setPinnedItems(updatedItems));
};

export const MovePinnedItemUp = (accountId: string, itemToMoveIndex: number): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  const existingPinnedItems: IStoreItem[] = getState().pinned.pinnedItems ?? [];
  if (itemToMoveIndex > 0 && existingPinnedItems.length > 0) {
    try {
      const updatedItems: IStoreItem[] = [...existingPinnedItems];
      [updatedItems[itemToMoveIndex], updatedItems[itemToMoveIndex - 1]] = [updatedItems[itemToMoveIndex - 1], updatedItems[itemToMoveIndex]];
      dispatch(updatePinnedItems(accountId, updatedItems));
    } catch (e) {
      logger(e, "pinnedItems.ts (MovePinnedItemUp)");
    }
  }
};

export const MovePinnedItemDown = (accountId: string, itemToMoveIndex: number): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  const existingPinnedItems: IStoreItem[] = getState().pinned.pinnedItems ?? [];
  if (itemToMoveIndex < existingPinnedItems.length - 1 && existingPinnedItems.length > 0) {
    try {
      const updatedItems: IStoreItem[] = [...existingPinnedItems];
      [updatedItems[itemToMoveIndex], updatedItems[itemToMoveIndex + 1]] = [updatedItems[itemToMoveIndex + 1], updatedItems[itemToMoveIndex]];
      dispatch(updatePinnedItems(accountId, updatedItems));
    } catch (e) {
      logger(e, "pinnedItems.ts (MovePinnedItemDown)");
    }
  }
};

//Non Redux-based functions. They just live here because it is grouped nicely
export const getPinnedFolderIndex = (driveId: string, itemId: string, pinnedItems: IStoreItem[]): number => {
  let pinnedIndex: number = -1;
  if (pinnedItems && pinnedItems.length > 0) {
    try {
      pinnedIndex = !itemId
        ? pinnedItems.findIndex(item => item.fileType == FileTypeEnum.folder && item.driveId && item.driveId === driveId && !item.itemId)
        : pinnedItems.findIndex(item => item.fileType == FileTypeEnum.folder && item.itemId && item.itemId === itemId);
    } catch (e) {
      logger(e, "pinnedItems.ts (getPinnedFolderIndex)");
    }
  }
  return pinnedIndex;
}

export const isFolderPinned = (driveId: string, itemId: string, pinnedItems: IStoreItem[]) => {
  return getPinnedFolderIndex(driveId, itemId, pinnedItems) > -1;
}

export const getPinnedFileIndex = (itemId: string, pinnedItems: IStoreItem[]): number => {
  let pinnedIndex: number = -1;
  if (pinnedItems && pinnedItems.length > 0) {
    try {
      pinnedIndex = pinnedItems.findIndex(item => item.fileType == FileTypeEnum.file && item.itemId && item.itemId === itemId);
    } catch (e) {
      logger(e, "pinnedItems.ts (getPinnedFileIndex)");
    }
  }
  return pinnedIndex;
}

export const isFilePinned = (itemId: string, pinnedItems: IStoreItem[]): boolean => {
  return getPinnedFileIndex(itemId, pinnedItems) > -1;
}

// const itemExist = (existingSavedItems: IStoreItem[], newItem: IStoreItem) => {
//   if (!existingSavedItems) return false;
//   return newItem.fileType === FileTypeEnum.file ? existingSavedItems.some(item => item.itemId === newItem.itemId) : folderExists(existingSavedItems, newItem);
// }

// const folderExists = (existingSavedItems: IStoreItem[], newItem: IStoreItem): boolean => {
//   const isRoot = !newItem?.itemId;
//   const result = isRoot ? existingSavedItems.some(item => item.driveId === newItem.driveId && item.name === newItem.name) :
//     existingSavedItems.some(item => item.driveId === newItem.driveId && item.itemId === newItem.itemId);

//   return result;
// }

type HomeAction = ReturnType<
  | typeof setPinnedItems
  | typeof resetPinnedItems
  | typeof setIsPinnedItemsLoading
>;

export function pinnedReducer(state = initialState, action: HomeAction): IPinnedItems {
  switch (action.type) {
    case SET_PINNED_IS_LOADING:
      return { ...state, isPinnedItemsLoading: action.payload };
    case SET_USER_PINNED_ITEMS:
      return { isPinnedItemsLoading: false, pinnedItems: action.payload };
    case RESET_USER_PINNED_ITEMS:
      return initialState;
    default:
      return state;
  }
}
